import { useMemo } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { useAsyncData } from '../../hooks/useAsyncData/useAsyncData.js';
import { AgentsService } from '../../services/agentsService.js';
import type { AgentVersion } from '../../types/agent.js';

export function useAgentListVersions(
  workspaceName: string | null,
  agentNames: string[],
) {
  const client = useHttpClient();
  const service = useMemo(() => new AgentsService(client), [client]);
  const namesKey = agentNames.join('\0');
  const fetcher = useMemo(() => {
    if (workspaceName === null || namesKey.length === 0) return null;
    const names = namesKey.split('\0');
    return async (signal: AbortSignal) => {
      const entries = await Promise.all(
        names.map(async (name) => {
          const versions = await service.listVersions(workspaceName, name, {
            signal,
          });
          return [name, versions[versions.length - 1]] as const;
        }),
      );
      return new Map<string, AgentVersion | undefined>(entries);
    };
  }, [workspaceName, namesKey, service]);

  const { data, isLoading, error, refresh } = useAsyncData(fetcher, {
    errorMessage: 'Failed to load agent versions',
  });
  return {
    latestVersions: data ?? new Map<string, AgentVersion | undefined>(),
    isLoading,
    error,
    refresh,
  };
}
